import { Box, Container, Stack, Typography, Button, Divider, Link } from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FacebookIcon from '@mui/icons-material/Facebook';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { brand } from '../themes/main_theme.js';

const FACEBOOK_URL = 'https://www.facebook.com/share/g/wk5dhjKGcjzCTcL4/?mibextid=K35XfP';
const EMAIL = 'saltandlightministriesguate@gmail.com';
const NEWSLETTER_URL = 'http://eepurl.com/ilnCGv';

const NAV = [
  { label: 'About us', path: '/about_us' },
  { label: 'Ministries', path: '/ministries' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Blog', path: '/blog' },
  { label: 'News', path: '/news' },
  { label: 'Donate', path: '/donate' },
  { label: 'Contact us', path: '/contact_us' },
];

/**
 * Site footer. The pages previously ended abruptly with no closing section,
 * no way to give, and no contact details below the fold.
 */
const Footer = () => {
  const navigate = useNavigate();

  return (
    <Box component="footer" sx={{ mt: { xs: 8, md: 12 }, bgcolor: '#2A1F1B', color: '#F5EBE3' }}>
      {/* Closing call to action */}
      <Box sx={{ bgcolor: 'primary.main', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ color: '#FFFFFF', mb: 1.5 }}>
            Be part of the work
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.88)', mb: 3.5, maxWidth: 560, mx: 'auto' }}>
            Every casita, food bag and classroom is made possible by people who give and pray.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" color="secondary" startIcon={<FavoriteIcon />} onClick={() => navigate('/donate')}>
              Give today
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.open(NEWSLETTER_URL, '_blank', 'noopener,noreferrer')}
              sx={{
                color: '#FFFFFF',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Subscribe to updates
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 8 }} justifyContent="space-between">
          <Box sx={{ maxWidth: 360 }}>
            <Typography variant="h5" sx={{ color: brand.goldGlow, mb: 1 }}>
              Salt and Light Ministries
            </Typography>
            <Typography sx={{ color: 'rgba(245,235,227,0.75)', fontSize: '0.95rem' }}>
              Serving the people of Guatemala since 2012 — sharing the Gospel through homes,
              food, education and friendship.
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" sx={{ color: brand.goldGlow }}>Explore</Typography>
            <Stack sx={{ mt: 1 }} spacing={0.75}>
              {NAV.map((item) => (
                <Link
                  key={item.path}
                  component="button"
                  onClick={() => navigate(item.path)}
                  underline="hover"
                  sx={{
                    color: 'rgba(245,235,227,0.82)',
                    textAlign: 'left',
                    fontSize: '0.95rem',
                    '&:hover': { color: '#FFFFFF' },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" sx={{ color: brand.goldGlow }}>Get in touch</Typography>
            <Stack sx={{ mt: 1.5 }} spacing={1.5} alignItems="flex-start">
              <Button
                startIcon={<FacebookIcon />}
                onClick={() => window.open(FACEBOOK_URL, '_blank', 'noopener,noreferrer')}
                sx={{ color: '#F5EBE3', px: 0, '&:hover': { background: 'none', color: '#FFFFFF' } }}
              >
                Facebook
              </Button>
              <Button
                startIcon={<MailIcon />}
                onClick={() => window.open(`mailto:${EMAIL}`)}
                sx={{ color: '#F5EBE3', px: 0, textTransform: 'none', '&:hover': { background: 'none', color: '#FFFFFF' } }}
              >
                Email us
              </Button>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 4, borderColor: 'rgba(245,235,227,0.15)' }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={{ color: 'rgba(245,235,227,0.55)', fontSize: '0.85rem', textAlign: { xs: 'center', sm: 'left' } }}>
            © {new Date().getFullYear()} Salt and Light Ministries · Guatemala
          </Typography>

          {/*
            Staff entrance to the CMS. Deliberately quiet — visitors have no use
            for it — but always reachable from any page.

            A plain href rather than navigate(): /admin is a separate lazy-loaded
            app mounted outside this router, so a real navigation boots it
            cleanly instead of relying on the public site's router to hand over.
          */}
          <Button
            component="a"
            href="/admin"
            startIcon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
            aria-label="Staff sign in to the site admin"
            sx={{
              color: 'rgba(245,235,227,0.55)',
              fontSize: '0.85rem',
              fontWeight: 500,
              px: 1,
              py: 0.5,
              '&:hover': { color: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Staff sign in
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
